import { IIndicatorFilterResponse } from '@kcftech/prometheustypescriptapiclient';
import {
    Dropdown,
    DropdownItemRenderer,
    DropdownLabelRenderer,
} from 'components/dropdown/Dropdown';
import { useInfiniteIndicatorFiltersDataSource } from 'custom_hooks/data_hooks/data_source/indicatorFilterDataSource';
import { useCallback, useEffect } from 'react';
import { getId } from 'utils/data';

const renderItem: DropdownItemRenderer<IIndicatorFilterResponse> = ({ item }) =>
    item.Name;

const renderLabel: DropdownLabelRenderer<IIndicatorFilterResponse> = ({ value }) =>
    value?.Name ?? 'Select a filter';

export type IndicatorFilterDropdownProps = Readonly<{
    /**
     * The location to narrow the results by.
     */
    locationId?: string;
    indicatorFilter?: IIndicatorFilterResponse;
    /**
     * Callback that is called when a new indicator filter is selected.
     */
    onChange: (value: IIndicatorFilterResponse) => void;
}>;

export function IndicatorFilterDropdown({
    locationId,
    indicatorFilter,
    onChange,
}: IndicatorFilterDropdownProps) {
    const {
        isLoading,
        hasNext,
        loadNext,
        results,
        filter,
        setFilter,
    } = useInfiniteIndicatorFiltersDataSource({
        name: 'indicator-filter-dropdown',
        defaultFilter: {
            locationId,
        },
    });

    useEffect(() => setFilter((filterValue) => ({ ...filterValue, locationId })), [
        locationId,
        setFilter,
    ]);

    // Function for changing the name filter state easily
    const setSearchText = useCallback(
        (searchText: string | undefined) => {
            setFilter((filterValue) => ({
                ...filterValue,
                nameSearch: searchText,
            }));
        },
        [setFilter]
    );

    return (
        <Dropdown<IIndicatorFilterResponse>
            data={{
                items: results,
                isLoading,
                hasMore: hasNext ?? false,
                onLoadMore: loadNext,
            }}
            dataKey={getId}
            value={indicatorFilter}
            renderItem={renderItem}
            renderLabel={renderLabel}
            onChange={onChange}
            filterable={{
                filterText: filter.nameSearch ?? '',
                onChange: setSearchText,
                placeholder: 'Search filters',
            }}
            trigger={{ className: 'mr-20' }}
            popup={{ width: '60rem' }}
        />
    );
}
